import React, { useContext, useEffect, useState } from "react";
import {
  Plus,
  Eye,
  X,
  ArrowDownLeft,
  ArrowUpRight,
  CreditCard,
  IndianRupee,
  AlertTriangle,
} from "lucide-react";
import DistributorMasterLayout from "../../../layouts/distributor/DistributorMasterLayout";
import "../../../assets/styles/distributor/DistributorPayments.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import { Image } from "react-bootstrap";
import Swal from "sweetalert2";

/* =============================================
   MOCK DATA
   ============================================= */
const FROM_CUSTOMERS = [
  {
    id: 1,
    name: "Ali Ahmed",
    amount: "PKR 3,600",
    method: "Cash",
    date: "2024-03-15",
    recordedBy: "Ali Ahmed (Customer)",
    status: "received",
  },
  {
    id: 2,
    name: "Sara Khan",
    amount: "PKR 2,400",
    method: "Online",
    date: "2024-03-14",
    recordedBy: "Sara Khan (Customer)",
    status: "received",
  },
  {
    id: 3,
    name: "Hassan Ali",
    amount: "PKR 4,800",
    method: "Cash",
    date: "2024-03-13",
    recordedBy: "Distributor",
    status: "received",
  },
  {
    id: 4,
    name: "Ahmed Raza",
    amount: "PKR 3,600",
    method: "—",
    date: "—",
    recordedBy: "—",
    status: "overdue",
  },
  {
    id: 5,
    name: "Zainab Shah",
    amount: "PKR 2,400",
    method: "Online",
    date: "2024-03-10",
    recordedBy: "Distributor",
    status: "pending",
  },
];

const TO_SUPPLIERS = [
  {
    id: 1,
    name: "Farmer Abdul",
    amount: "PKR 35,000",
    method: "Bank Transfer",
    date: "2024-03-10",
    recordedBy: "Distributor",
    status: "paid",
  },
  {
    id: 2,
    name: "Green Valley Dairy",
    amount: "PKR 24,000",
    method: "Cash",
    date: "2024-03-08",
    recordedBy: "Distributor",
    status: "paid",
  },
  {
    id: 3,
    name: "Punjab Milk Farm",
    amount: "PKR 16,000",
    method: "Bank Transfer",
    date: "2024-03-05",
    recordedBy: "—",
    status: "pending",
  },
];

/* =============================================
   RECORD PAYMENT MODAL
   ============================================= */
const RecordPaymentModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    type: "Payment from Customer",
    name: "",
    amount: "",
    method: "Cash",
  });

  const handle = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const isCustomer = form.type === "Payment from Customer";

  return (
    <div className="distPay-overlay" onClick={onClose}>
      <div className="distPay-modal" onClick={(e) => e.stopPropagation()}>
        <button className="distPay-modal-close" onClick={onClose}>
          <X size={18} />
        </button>
        <div className="distPay-modal-header">
          <h4>Record Payment</h4>
          <p>Record a payment from customer</p>
        </div>

        <div className="distPay-form-field">
          <label>Payment Type</label>
          <select
            className="distPay-form-select"
            name="type"
            value={form.type}
            onChange={handle}
          >
            <option>Payment from Customer</option>
            {/* <option>Payment to Supplier</option> */}
          </select>
        </div>

        <div className="distPay-form-field">
          <label>{isCustomer ? "Customer Name" : "Supplier Name"}</label>
          <input
            className="distPay-form-input"
            name="name"
            placeholder="Select or type name"
            value={form.name}
            onChange={handle}
          />
        </div>

        <div className="distPay-form-field">
          <label>Amount (PKR)</label>
          <input
            className="distPay-form-input"
            name="amount"
            placeholder="e.g. 3600"
            value={form.amount}
            onChange={handle}
          />
        </div>

        <div className="distPay-form-field">
          <label>Payment Method</label>
          <select
            className="distPay-form-select"
            name="method"
            value={form.method}
            onChange={handle}
          >
            <option>Cash</option>
            <option>JazzCash</option>
            <option>EasyPaisa</option>
            <option>Bank Transfer</option>
          </select>
        </div>

        <div className="distPay-modal-footer">
          <button className="distPay-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="distPay-btn-green"
            onClick={() => {
              onSave(form);
              onClose();
            }}
          >
            Record Payment
          </button>
        </div>
      </div>
    </div>
  );
};

/* =============================================
   PAYMENT DETAILS MODAL
   ============================================= */
const PaymentDetailsModal = ({ payment, onClose, onGenerateDispute }) => {
  const { baseUrlForImage } = useContext(AppContext);
  if (!payment) return null;

  const statusClass =
    {
      paid: "distPay-badge-paid",
      received: "distPay-badge-received",
      pending: "distPay-badge-pending",
      overdue: "distPay-badge-overdue",
    }[payment.status] || "distPay-badge-pending";

  const statusLabel =
    payment.status.charAt(0).toUpperCase() + payment.status.slice(1);

  return (
    <div className="distPay-overlay" onClick={onClose}>
      <div className="distPay-modal" onClick={(e) => e.stopPropagation()}>
        <button className="distPay-modal-close" onClick={onClose}>
          <X size={18} />
        </button>
        <div className="distPay-modal-header">
          <h4>Payment Details</h4>
        </div>

        <div className="distPay-details-grid">
          <div className="distPay-detail-field">
            <label>Name:</label>
            <strong>{payment.mwcustomer.fullName}</strong>
          </div>
          <div className="distPay-detail-field">
            <label>Amount:</label>
            <strong>{payment.amount}</strong>
          </div>
          <div className="distPay-detail-field">
            <label>Method:</label>
            <strong>{payment.method}</strong>
          </div>
          <div className="distPay-detail-field">
            <label>Date:</label>
            <strong>{payment.created_at}</strong>
          </div>
          <div className="distPay-detail-field">
            <label>Recorded By:</label>
            <strong>{payment.recordedby}</strong>
          </div>
          <div className="distPay-detail-field">
            <label>Status:</label>
            <span className={`distPay-badge ${statusClass}`}>
              {statusLabel}
            </span>
          </div>
        </div>

        <div className="distPay-receipt-box">
          {payment.reciept ? (
            <Image
              src={`${baseUrlForImage}${payment.reciept}`}
              className="payment-reciept"
            ></Image>
          ) : (
            <>
              <p>Receipt / Proof of Payment</p>
              <span>No receipt uploaded</span>
            </>
          )}
        </div>

        {/* Show Generate Dispute only for customer payments */}
        {/* {payment.recordedby?.includes("mwustomer") && ( */}
        <button
          className="distPay-btn-red"
          style={{ width: "100%", marginBottom: 10 }}
          onClick={() => {
            onClose();
            onGenerateDispute(payment);
          }}
        >
          Generate Dispute
        </button>

        <button className="distPay-btn-close" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

/* =============================================
   GENERATE DISPUTE MODAL
   ============================================= */
const GenerateDisputeModal = ({ payment, onClose }) => {
  const [reason, setReason] = useState("");
  const { loggedInDistributor, baseUrl } = useContext(AppContext);

  if (!payment) return null;

  const submitDispute = () => {
    console.log(payment.mwcustomer.id,"this is log from submit payment dispute")
    const formData = new FormData();
    formData.append("mwdistributor_id", loggedInDistributor.id);
    formData.append("mwcustomer_id", payment.mwcustomer.id);
    formData.append("subject", reason);
    formData.append("raisedby", "distributor");
    formData.append("raisedto", "customer");

    axios
      .post(`${baseUrl}/distributor/dispute/submitDispute`, formData, {
        headers: { "Content-type": "multipart-formdata" },
      })
      .then((res) => {
        console.log(res.data)
        if(res.data.success == true){
          Swal.fire({
            'title' : 'Wow',
            'text': res.data.message,
            'confirmButtonText' : 'Okay!' 
          })
          onClose();
        }
      })
      .catch((err) => {
        console.log(err)
      });
  };

  return (
    <div className="distPay-overlay" onClick={onClose}>
      <div className="distPay-modal" onClick={(e) => e.stopPropagation()}>
        <button className="distPay-modal-close" onClick={onClose}>
          <X size={18} />
        </button>
        <div className="distPay-modal-header">
          <h4>Generate Dispute</h4>
          <p>Dispute payment from {payment.mwcustomer.fullName}</p>
        </div>

        <div className="distPay-dispute-info">
          Customer: {payment.mwcustomer.fullName}
          <br />
          Amount: {payment.amount}
        </div>

        <div className="distPay-form-field">
          <label>Reason</label>
          <textarea
            className="distPay-form-textarea"
            placeholder="e.g. Payment not received..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="distPay-modal-footer">
          <button className="distPay-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="distPay-btn-red" onClick={submitDispute}>
            Submit Dispute
          </button>
        </div>
      </div>
    </div>
  );
};

/* =============================================
   MAIN PAGE CONTENT
   ============================================= */
const PaymentsContent = () => {
  const [activeTab, setActiveTab] = useState("customers");
  const [showRecord, setShowRecord] = useState(false);
  const [viewPayment, setViewPayment] = useState(null);
  const [disputePayment, setDisputePayment] = useState(null);
  const [fromCustomers, setFromCustomers] = useState(FROM_CUSTOMERS);
  const [payments, setPayments] = useState([]);
  const [toSuppliers] = useState(TO_SUPPLIERS);

  const totalReceived = "PKR 67,000";
  const totalPaid = "PKR 59,000";
  const pendingCust = "PKR 14,200";
  const dueSuppliers = "PKR 16,000";

  const currentList = activeTab === "customers" ? fromCustomers : toSuppliers;
  const isCustomers = activeTab === "customers";

  const { baseUrl, loggedInDistributor } = useContext(AppContext);

  const handleRecordSave = (form) => {
    const isFromCustomer = form.type === "Payment from Customer";
    const newEntry = {
      id: Date.now(),
      name: form.name || "Unknown",
      amount: `PKR ${form.amount}`,
      method: form.method,
      date: new Date().toISOString().slice(0, 10),
      recordedBy: isFromCustomer ? `${form.name} (Customer)` : "Distributor",
      status: "pending",
    };
    if (isFromCustomer) {
      setFromCustomers((prev) => [newEntry, ...prev]);
      setActiveTab("customers");
    } else {
      setActiveTab("suppliers");
    }
  };

  const viewAllPayments = () => {
    console.log("function called view all payments", loggedInDistributor);
    const formData = new FormData();
    formData.append("mwdistributor_id", loggedInDistributor.id);
    axios
      .post(`${baseUrl}/distributor/payment/viewAllPayments`, formData, {
        headers: { "Content-type": "multipart-formdata" },
      })
      .then((res) => {
        console.log("resposne called", res.data);
        if (res.data) {
          setPayments(res.data.payments);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    viewAllPayments();
  }, []);

  return (
    <div className="distPay-page">
      {/* Header */}
      <div className="distPay-header-row">
        <div className="distPay-title">
          <h2>Payments</h2>
          <p>Track all incoming and outgoing payments</p>
        </div>
        <button
          className="distPay-record-btn"
          onClick={() => setShowRecord(true)}
        >
          <Plus size={15} /> Record Payment
        </button>
      </div>

      {/* Stats */}
      {/* <div className="distPay-stats-row"> */}
        {/* <div className="distPay-stat-item">
          <div className="distPay-stat-icon green">
            <ArrowDownLeft size={20} />
          </div>
          <div className="distPay-stat-text">
            <span>Total Received</span>
            <h4>{totalReceived}</h4>
          </div>
        </div> */}
        {/* <div className="distPay-stat-item">
          <div className="distPay-stat-icon blue"><ArrowUpRight size={20}/></div>
          <div className="distPay-stat-text">
            <span>Total Paid</span>
            <h4>{totalPaid}</h4>
          </div>
        </div> */}
        {/* <div className="distPay-stat-item"> */}
          {/* <div className="distPay-stat-icon orange">
            <CreditCard size={20} />
          </div> */}
          {/* <div className="distPay-stat-text">
            <span>Pending from Customers</span>
            <h4>{pendingCust}</h4>
          </div> */}
        {/* </div> */}
        {/* <div className="distPay-stat-item">
          <div className="distPay-stat-icon red"><IndianRupee size={20}/></div>
          <div className="distPay-stat-text">
            <span>Due to Suppliers</span>
            <h4>{dueSuppliers}</h4>
          </div>
        </div> */}
    

      {/* Tabs */}
        {/* <button
          className={`distPay-tab-btn ${activeTab === "customers" ? "active" : ""}`}
          onClick={() => setActiveTab("customers")}
        >
          From Customers
        </button> */}
        {/* <button
          className={`distPay-tab-btn ${activeTab === "suppliers" ? "active" : ""}`}
          onClick={() => setActiveTab("suppliers")}
        >
          To Suppliers
        </button> */}
      {/* </div> */}

      {/* Table */}
      <div className="distPay-table-card">
        <div className="distPay-table-wrap">
          <table className="distPay-table">
            <thead>
              <tr>
                <th>{isCustomers ? "Customer" : "Supplier"}</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Date</th>
                <th>Recorded By</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* {currentList.map((row) => { */}
              {payments.map((row) => {
                const statusClass =
                  {
                    paid: "distPay-badge-paid",
                    received: "distPay-badge-received",
                    pending: "distPay-badge-pending",
                    overdue: "distPay-badge-overdue",
                  }[row.status] || "distPay-badge-pending";

                return (
                  <tr key={row.id}>
                    <td>
                      <strong style={{ fontWeight: 600 }}>
                        {row.mwcustomer.fullName}
                      </strong>
                    </td>
                    <td>{row.amount}</td>
                    <td>
                      {row.method === "—" ? (
                        <span className="distPay-dash">—</span>
                      ) : (
                        row.method
                      )}
                    </td>
                    <td>
                      {row.date === "—" ? (
                        <span className="distPay-dash">—</span>
                      ) : (
                        row.created_at
                      )}
                    </td>
                    <td style={{ fontSize: "0.82rem", opacity: 0.7 }}>
                      {row.recordedBy === "—" ? (
                        <span className="distPay-dash">—</span>
                      ) : (
                        row.recordedby
                      )}
                    </td>
                    <td>
                      <span className={`distPay-badge ${statusClass}`}>
                        {row.status.charAt(0).toUpperCase() +
                          row.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="distPay-eye-btn"
                        title="View Details"
                        onClick={() => setViewPayment(row)}
                      >
                        <Eye size={17} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showRecord && (
        <RecordPaymentModal
          onClose={() => setShowRecord(false)}
          onSave={handleRecordSave}
        />
      )}
      {viewPayment && (
        <PaymentDetailsModal
          payment={viewPayment}
          onClose={() => setViewPayment(null)}
          onGenerateDispute={(p) => setDisputePayment(p)}
        />
      )}
      {disputePayment && (
        <GenerateDisputeModal
          payment={disputePayment}
          onClose={() => setDisputePayment(null)}
        />
      )}
    </div>
  );
};

const DistributorPayments = () => (
  <DistributorMasterLayout>
    <PaymentsContent />
  </DistributorMasterLayout>
);

export default DistributorPayments;
